import strings from 'common/Translation/Translate';
import Debounce from 'utils/validators/debounce';
import axiosInstance from 'utils/axios';
import { validateInput } from 'utils/validators/Validators';
import { FormValidators } from 'components/Form';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Fragment, useState, useCallback, useEffect } from 'react';

interface OwnProps {
  url: any;
  field: string;
  disabled?: boolean;
  placeholder?: string;
  typeValue?: string;
  onChange: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  label?: string;
  size?: any;
  sx?: any;
  id?: any;
  value: any;
}

const AutocompleteDropdown = ({
  url,
  field,
  disabled,
  placeholder,
  onChange,
  validators,
  hasError,
  fieldError,
  label,
  size = 'small',
  sx,
  id,
  value,
}: OwnProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>([]);
  const [inputValue, setInputValue] = useState<any>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{
    error?: boolean;
    message?: string;
  } | null>({ error: hasError, message: fieldError as string });

  useEffect(() => {
    if (options.length < 1) {
      setOpen(false);
    }
  }, [options]);
  const handleChange = (_event: any, newValue: any) => {
    if (newValue === null) {
      setInputValue('');
      const inputError = validateInput(validators, '');
      setError(inputError);
      onChange('', field, inputError);
      return;
    }
    const val = typeof newValue === 'object' ? newValue[field] : newValue;
    const inputError = validateInput(validators, val);
    setError(inputError);
    if (typeof newValue === 'object') {
      setInputValue(newValue[field] || '');
    }
    onChange(newValue, field, inputError);
  };
  const handleInputChange = (_event: any, newInputValue: string | null, reason?: string) => {
    if (reason === 'reset') {
      return;
    }
    const nextValue = newInputValue ?? '';
    const inputError = validateInput(validators, nextValue);
    setError(inputError);
    setInputValue(nextValue);
    onChange(nextValue, field, inputError);
  };
  const customMessage = () => (!error && hasError && fieldError ? strings.requiredText : '');
  const getOptions = useCallback(
    async (val: string) => {
      try {
        if (val !== '') {
          setLoading(true);
          const { data } = await axiosInstance.get(url(val));
          setLoading(false);
          setOptions(data);
        } else {
          setOptions([]);
        }
      } catch (error: any) {
        setLoading(false);
      }
    },
    [url],
  );

  const optimizedFn = useCallback(
    Debounce((val: string) => getOptions(val), 1000),
    [],
  ) as any;

  useEffect(() => {
    const next = typeof value === 'string' ? value : value?.[field] || '';
    setInputValue(next);
  }, [field, value]);

  useEffect(() => {
    optimizedFn(typeof inputValue === 'string' ? inputValue : '');
  }, [optimizedFn, inputValue]);

  return (
    <Autocomplete
      id={id}
      freeSolo
      clearOnBlur={false}
      sx={{ ...sx }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      value={typeof value === 'string' ? value : value?.[field] || null}
      inputValue={inputValue}
      // placeholder={placeholder}
      disabled={disabled}
      isOptionEqualToValue={(option: any, selected: any) =>
        typeof selected === 'string' ? option?.firstName === selected : option?.id != null && option.id === selected?.id
      }
      getOptionLabel={(option: any) =>
        typeof option === 'string' ? option : option?.firstName || inputValue || ''
      }
      filterOptions={loaded => loaded}
      renderOption={(props, option: any) => {
        const name = [option?.firstName, option?.lastName].filter(Boolean).join(' ');
        const company = option?.companyName?.trim();
        const contact = [option?.email, option?.phone?.value].filter(Boolean).join(' · ');
        const role = String(option?.role || '').toUpperCase();
        const isCustomer = role === 'ROLE_CUSTOMER';
        const isAdmin = Boolean(role) && !isCustomer;
        return (
          <Box component="li" {...props} sx={{ alignItems: 'flex-start !important', py: 1 }}>
            <Box sx={{ minWidth: 0, width: '100%' }}>
              {name ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {isCustomer ? (
                    <StarBorder sx={{ fontSize: 16, color: 'inherit', flexShrink: 0 }} titleAccess="Customer" />
                  ) : null}
                  {isAdmin ? (
                    <PeopleOutlined sx={{ fontSize: 16, color: 'inherit', flexShrink: 0 }} titleAccess="Admin" />
                  ) : null}
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, minWidth: 0 }}>
                    {name}
                  </Typography>
                </Box>
              ) : null}
              {company ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                  {company}
                </Typography>
              ) : null}
              {contact ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                  {contact}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      componentsProps={{
        paper: { sx: { minWidth: 280 } },
      }}
      options={options}
      loading={loading}
      onFocus={() => setOpen(false)}
      onChange={(event, newValue) => {
        handleChange(value, newValue);
      }}
      onInputChange={(value, newInputValue) => {
        handleInputChange(value, newInputValue);
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          size={size}
          label={label}
          InputLabelProps={{
            title: typeof label === 'string' ? label : undefined,
          }}
          helperText={error && error.error ? error.message : customMessage()}
          error={error?.error || (hasError && fieldError) ? true : false}
          autoComplete="new-password"
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',
            name: 'bbr-booking-first-name',
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
            'data-form-type': 'other',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default AutocompleteDropdown;
